import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'

import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { DateCell, MutedCell, NameCell, StatusChip } from '@/components/TableCells'
import { localeLabel } from '@/data/userLocales'
import type { EmailTranslation } from '@/services/networks/emailTranslationsApi'

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  messageDefinition: 'messageDefinition',
  locale: 'locale',
  emailTemplate: 'emailTemplate.name',
  objectState: 'objectState',
  team: 'team.name',
  updateTime: 'updateTime',
  createTime: 'createTime',
}

interface EmailTranslationTableProps {
  emailTranslations: EmailTranslation[]
  loading?: boolean
  emptyMessage?: string
  selection?: EmailTranslation[]
  onSelectionChange?: (emailTranslations: EmailTranslation[]) => void
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function EmailTranslationTable({
  emailTranslations,
  loading,
  emptyMessage,
  selection,
  onSelectionChange,
  sort,
  onSortChange,
}: EmailTranslationTableProps) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const selectable = !!onSelectionChange

  const columns: ColumnDef<EmailTranslation, unknown>[] = [
    ...(selectable ? [selectionColumn<EmailTranslation>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 200 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    {
      accessorKey: 'messageDefinition',
      header: t('emailTranslations.emailType'),
      meta: { minWidth: 240 },
      cell: ({ row }) => (
        <MutedCell value={t(`emailTranslations.types.${row.original.messageDefinition}`)} />
      ),
    },
    {
      accessorKey: 'locale',
      header: t('emailTranslations.locale'),
      size: 170,
      cell: ({ row }) => (
        <MutedCell
          value={
            row.original.locale
              ? localeLabel(row.original.locale, i18n.language)
              : t('emailTranslations.allLocales')
          }
        />
      ),
    },
    {
      id: 'emailTemplate',
      accessorFn: (row) => row.emailTemplate?.name ?? '',
      header: t('emailTranslations.emailTemplate'),
      size: 170,
      cell: ({ row }) => (
        <MutedCell
          value={row.original.emailTemplate?.name || t('emailTranslations.defaultTemplate')}
        />
      ),
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => <StatusChip state={row.original.objectState} />,
    },
    optionalColumn<EmailTranslation>({
      accessorKey: 'description',
      header: t('common.description'),
      enableSorting: false,
      meta: { minWidth: 220 },
      cell: ({ row }) => <MutedCell value={row.original.description} />,
    }),
    optionalColumn<EmailTranslation>({
      id: 'applications',
      accessorFn: (row) => row.applications.join(),
      header: t('emailTranslations.applications'),
      size: 160,
      cell: ({ row }) => (
        <MutedCell
          value={
            row.original.applications.length
              ? row.original.applications
                  .map((app) => t(`emailTranslations.apps.${app}`))
                  .join(', ')
              : t('emailTranslations.allApplications')
          }
        />
      ),
    }),
    optionalColumn<EmailTranslation>({
      id: 'team',
      accessorFn: (row) => row.team?.name ?? '',
      header: t('emailTemplates.team'),
      size: 160,
      cell: ({ row }) => (
        <MutedCell value={row.original.team?.name || t('emailTemplates.noTeam')} />
      ),
    }),
    optionalColumn<EmailTranslation>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<EmailTranslation>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={emailTranslations}
      loading={loading}
      emptyMessage={emptyMessage ?? t('emailTranslations.noEmailTranslationsFound')}
      columnsKey="emailTranslations"
      onRowClick={(row) => navigate(`/admin/email-translations/${row.id}`)}
      {...(selectable ? { selection, onSelectionChange } : {})}
      serverSort={
        onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
      }
    />
  )
}
